import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { filterChatMessages } from '../../utils/alertsConversations';

class ChatDependencyError extends Error {
  constructor(...params) {
    super(...params);
    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, ChatDependencyError);
    }
    this.name = 'ChatDependencyError';
  }
  toString() {
    return `${this.name}: ${this.message}`;
  }
}

// Hook for determining whether or not to render the automated chat history
export const useShouldRenderChatContainer = () => {
  const { chats, incidents } = useSelector(state => {
    const chat = state.chat;
    const incidents = state.incidents;
    if (!chat) {
      throw new ChatDependencyError(
        'The chat slice is missing in redux state.',
      );
    } else if (!incidents) {
      throw new ChatDependencyError(
        'The incidents slice is missing in redux state.',
      );
    }
    return { chats: chat.chats, incidents };
  });

  const [currentConversation, setCurrentConversation] = useState(null);
  const [currentAlert, setCurrentAlert] = useState(null);

  useEffect(() => {
    const alertId = incidents.selectedAlertID;
    const alert = incidents.alerts[incidents.selectedAlertID];

    if (!alertId) {
      setCurrentConversation(null);
      setCurrentAlert(null);
      return;
    }
    if (chats[alertId]) {
      setCurrentConversation(chats[alertId]);
    }
    if (alert) {
      setCurrentAlert(alert);
    }
  }, [incidents, chats]);

  const filteredConversationMessages = useMemo(() => {
    return filterChatMessages(currentConversation, currentAlert?.created_time);
  }, [currentConversation, currentAlert]);

  const shouldRenderChatHistory = useMemo(() => {
    return filteredConversationMessages.length > 0;
  }, [filteredConversationMessages]);
  return shouldRenderChatHistory;
};

// Hook for determining whether or not to render the live chat with TalkJS
export const useShouldRenderTalkJS = currentAlert => {
  const isChatEnabled = currentAlert?.is_chat_enabled;
  const isMediaEnabled = currentAlert?.is_media_enabled;
  const shouldRenderTalkJS = useMemo(() => {
    return isChatEnabled || isMediaEnabled;
  }, [isChatEnabled, isMediaEnabled]);

  return shouldRenderTalkJS;
};

// Hook for getting the ADR for the current conversation to render the Suggested
// Script Responses tab
export const useSelectedConversationAdr = () => {
  const { chats, selectedAlertID } = useSelector(state => {
    const chat = state.chat;
    if (!chat) {
      throw new ChatDependencyError(
        'The chat slice is missing in redux state.',
      );
    }
    return chat;
  });

  const [currentConversation, setCurrentConversation] = useState(null);

  useEffect(() => {
    if (!selectedAlertID) {
      setCurrentConversation(null);
      return;
    }
    if (chats[selectedAlertID]) {
      setCurrentConversation(chats[selectedAlertID]);
    }
  }, [chats, selectedAlertID]);

  const currentConversationAdr = useMemo(() => {
    return currentConversation?.adr || [];
  }, [currentConversation]);
  return currentConversationAdr;
};

// Hook for getting the current conversation data
export const useCurrentConversation = () => {
  const { chats, selectedAlertID } = useSelector(state => {
    const chat = state.chat;
    if (!chat) {
      throw new ChatDependencyError(
        'The chat slice is missing in redux state.',
      );
    }
    return chat;
  });
  const [currentConversation, setCurrentConversation] = useState(null);

  useEffect(() => {
    if (!selectedAlertID) {
      setCurrentConversation(null);
      return;
    }
    if (chats[selectedAlertID]) {
      setCurrentConversation(chats[selectedAlertID]);
    }
  }, [chats, selectedAlertID]);
  return currentConversation;
};

/**
 * Hook to get AlertDataEvent's spatial layer intersect
 * @param {string} alertID
 * @returns spatial layer intersect object or undefined
 */
export const useSpatialLayerIntersect = alertID => {
  const spatiaLayerIntersect = useSelector(
    state => state.alertDataEvent.alerts?.[alertID]?.spatial_layer_intersect,
  );
  return spatiaLayerIntersect;
};

/**
 * Hook to get optional features from AlertDataEvent block
 * @param {string} alertID
 * @returns optional features object or empty object
 */

export const useOptionalFeatures = alertID => {
  const optionalFeatures = useSelector(
    state => state.alertDataEvent.alerts?.[alertID]?.optionalFeatures,
  );
  return optionalFeatures || {};
};
